import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  Button, 
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Modal,
  IconButton,
  Tooltip,
  Snackbar,
  Badge
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import api from '../api/axios';
import ModuleSettingsModal from './ModuleSettingsModal';
import LearningContentPDFViewer from './LearningContentPDFViewer';
import BokPageViewerModal from './BokPageViewerModal';
import LearningBuilder from './LearningBuilder';
import TaskAgentBuilder from './TaskAgentBuilder';
import SavedContentModal from './SavedContentModal';
import TaskAssessmentWizardModal from './TaskAssessmentWizardModal';
import ModuleAssessmentsTab from './ModuleAssessmentsTab';
import ModuleAssessmentAuditPanel from './ModuleAssessmentAuditPanel';
import ProgramExamsPanel from './ProgramExamsPanel';

import { filterChildrenForContentGeneration } from '../utils/contentGenerationSubtasks';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';
import { downloadDocx } from '../utils/downloadDocx';

function formatDocumentChange(entry) {
  if (!entry) return '';
  const when = entry.ts ? new Date(entry.ts).toLocaleString() : '';
  const action = String(entry.action || 'change').replace(/_/g, ' ');
  const bits = [];
  if (entry.word_count != null) bits.push(`${Number(entry.word_count).toLocaleString()} words`);
  if (entry.page_count != null) bits.push(`${entry.page_count} page${entry.page_count === 1 ? '' : 's'}`);
  if (entry.existing_word_count != null && entry.incoming_word_count != null) {
    bits.push(`kept ${Number(entry.existing_word_count).toLocaleString()} vs incoming ${Number(entry.incoming_word_count).toLocaleString()}`);
  }
  return `${when}${when ? ' — ' : ''}${action}${bits.length ? ` (${bits.join(', ')})` : ''}`;
}

export default function ContentCreationStep({ qualificationId }) {
  const [activeTab, setActiveTab] = useState(0);
  const [agentsBuilderTab, setAgentsBuilderTab] = useState(false);
  const [modules, setModules] = useState([]);
  const [assignedItems, setAssignedItems] = useState({}); // { moduleCode: { KT: [...], PS: [...] } }
  const [generatedContent, setGeneratedContent] = useState({}); // { subtaskCode: {...} }
  const [generating, setGenerating] = useState({}); // { subtaskCode: true/false }
  const [generatingThumbnail, setGeneratingThumbnail] = useState({}); // { subtaskCode: true/false }
  const [generatingPages, setGeneratingPages] = useState({}); // { subtaskCode: [pages] }
  const [thumbnails, setThumbnails] = useState({}); // { subtaskCode: thumbnailUrl }
  const [moduleSubTabs, setModuleSubTabs] = useState({}); // { moduleId: 0/1/2 } Knowledge / Assessments / Audit
  const [taskApprovals, setTaskApprovals] = useState({}); // task_code -> ISO date string | null
  const [moduleAssessments, setModuleAssessments] = useState({}); // moduleId -> assessments[]
  const [wizardState, setWizardState] = useState({ open: false, task: null, moduleId: null });
  const [taskAssessmentFileStatus, setTaskAssessmentFileStatus] = useState({}); // task_code -> has OpenAI file
  const [syncSnackbar, setSyncSnackbar] = useState({ open: false, message: '' });
  const [programAssessmentListVersion, setProgramAssessmentListVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewingPdf, setViewingPdf] = useState({}); // { subtaskCode: true } while Chromium is rendering
  const [downloadingWord, setDownloadingWord] = useState({});
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: null, title: '', subtaskCode: '' });
  const [thumbnailModal, setThumbnailModal] = useState({ open: false, url: null, title: '' });
  const [settingsModal, setSettingsModal] = useState({ open: false, moduleCode: '', moduleTitle: '' });
  const [bokPageViewer, setBokPageViewer] = useState({ open: false, subtaskCode: '', title: '' });
  const [qualification, setQualification] = useState(null);
  const [savedDocuments, setSavedDocuments] = useState({ subtaskCodes: [], documents: {} }); // KTs that have saved BOK content
  const [savedContentModal, setSavedContentModal] = useState({
    open: false,
    title: '',
    moduleName: '',
    content: '',
    runId: null,
    subtaskCode: null,
    referencesPendingCompletion: false,
    referencesDesignerNotice: null,
    wordCount: 0,
    pageCount: 0,
    changeLog: []
  });
  
  // Fetch ELO outcomes and their assignments from Step 3
  useEffect(() => {
    if (!qualificationId) return;
    
    (async () => {
      try {
        setLoading(true);
        
        // Fetch qualification info
        const qualRes = await api.get(`/qualifications/${qualificationId}`);
        setQualification(qualRes.data);
        
        // Fetch outcomes (ELOs from Step 3)
        const outcomesRes = await api.get(`/qualifications/${qualificationId}/outcomes`);
        const outcomes = outcomesRes.data?.outcomes || [];
        
        // Sort by ID to maintain order
        const sortedOutcomes = outcomes.sort((a, b) => (a.id || 0) - (b.id || 0));
        setModules(sortedOutcomes);
        
        // Fetch requirement nodes (to get KT/PS details)
        const reqRes = await api.get(`/qualifications/${qualificationId}/requirement-nodes`);
        const reqNodes = reqRes.data?.nodes || [];
        
        // For each ELO, find assigned KT and PS parent items, then get their children
        const assignments = {};
        sortedOutcomes.forEach(outcome => {
          const moduleAssignments = { KT: [], PS: [] };
          
          // Collect parent codes from both sources
          const ktParentCodes = new Set();
          const psParentCodes = new Set();
          
          // Method 1: Use assignment_meta if available
          const assignmentMeta = outcome.assignment_meta || outcome._assignment_meta || {};
          Object.entries(assignmentMeta).forEach(([code, assignment]) => {
            if (assignment.type === 'KT') {
              ktParentCodes.add(code);
            } else if (assignment.type === 'PS') {
              psParentCodes.add(code);
            }
          });
          
          // Method 2: Also check legacy knowledge_tasks and practical_work arrays
          const ktCodes = outcome.knowledge_tasks || [];
          const psCodes = outcome.practical_work || [];
          
          ktCodes.forEach(code => ktParentCodes.add(code));
          psCodes.forEach(code => psParentCodes.add(code));
          
          // Now add the parent KT/PS items themselves (not their children)
          ktParentCodes.forEach(parentCode => {
            const parentNode = reqNodes.find(r => r.code === parentCode);
            if (parentNode) {
              // Get all children for the hierarchy
              const children = reqNodes.filter(r => r.parent_code === parentCode);
              moduleAssignments.KT.push({
                code: parentNode.code,
                title: parentNode.title || parentNode.code,
                description: parentNode.description || parentNode.text || '',
                parent_code: null, // This is a parent item
                parent_title: null,
                type: 'KT',
                node_type: parentNode.node_type,
                credits: parentNode.credits || 0,
                nqf_level: parentNode.nqf_level != null ? parentNode.nqf_level : null,
                children: children.map(child => ({
                  code: child.code,
                  title: child.title || child.code,
                  description: child.description || child.text || '',
                  node_type: child.node_type,
                  credits: child.credits || 0,
                  nqf_level: child.nqf_level != null ? child.nqf_level : null
                }))
              });
            }
          });
          
          psParentCodes.forEach(parentCode => {
            const parentNode = reqNodes.find(r => r.code === parentCode);
            if (parentNode) {
              // Get all children for the hierarchy
              const children = reqNodes.filter(r => r.parent_code === parentCode);
              moduleAssignments.PS.push({
                code: parentNode.code,
                title: parentNode.title || parentNode.code,
                description: parentNode.description || parentNode.text || '',
                parent_code: null, // This is a parent item
                parent_title: null,
                type: 'PS',
                node_type: parentNode.node_type,
                credits: parentNode.credits || 0,
                nqf_level: parentNode.nqf_level != null ? parentNode.nqf_level : null,
                children: children.map(child => ({
                  code: child.code,
                  title: child.title || child.code,
                  description: child.description || child.text || '',
                  node_type: child.node_type,
                  credits: child.credits || 0,
                  nqf_level: child.nqf_level != null ? child.nqf_level : null
                }))
              });
            }
          });
          
          assignments[outcome.id] = moduleAssignments;
        });
        
        setAssignedItems(assignments);
        
        // Fetch existing generated content
        const contentRes = await api.get(`/qualifications/${qualificationId}/learning-content`);
        const existingContent = contentRes.data?.content || [];
        const contentMap = {};
        existingContent.forEach(item => {
          contentMap[item.subtask_code] = item;
        });
        setGeneratedContent(contentMap);
        
        // Fetch existing thumbnails
        try {
          const thumbnailsRes = await api.get(`/qualifications/${qualificationId}/learning-content/thumbnails`);
          if (thumbnailsRes.data?.success) {
            setThumbnails(thumbnailsRes.data.thumbnails);

          }
        } catch (thumbnailsError) {

          // Don't fail the entire load if thumbnails fail
        }
        
        setLoading(false);
      } catch (e) {

        setLoading(false);
      }
    })();
  }, [qualificationId]);

  // Always fetch saved-documents when qualificationId is set (e.g. on refresh) so "Open saved document" shows
  useEffect(() => {
    if (!qualificationId) return;
    (async () => {
      try {
        const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
        if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
          setSavedDocuments({
            subtaskCodes: savedListRes.data.subtaskCodes,
            documents: savedListRes.data.documents || {}
          });
        } else {
          setSavedDocuments({ subtaskCodes: [], documents: {} });
        }
      } catch {
        setSavedDocuments({ subtaskCodes: [], documents: {} });
      }
    })();
  }, [qualificationId]);

  const refreshModuleLearningMeta = useCallback(
    async (moduleId) => {
      if (!qualificationId || moduleId == null) return;
      const assign = assignedItems[moduleId] || { KT: [], PS: [] };
      const items = [...(assign.KT || []), ...(assign.PS || [])];
      const codes = items.map((i) => i.code).filter(Boolean);
      if (codes.length) {
        try {
          const appr = await api.get(
            `/qualifications/${qualificationId}/learning-content/bok-documents/learning-approvals`,
            { params: { task_codes: codes.join(',') } }
          );
          if (appr.data?.success && appr.data.approvals) {
            setTaskApprovals((prev) => ({ ...prev, ...appr.data.approvals }));
          }
        } catch (_) {}
      }
      try {
        const ass = await api.get(
          `/qualifications/${qualificationId}/learning-content/task-assessments`,
          { params: { module_id: moduleId } }
        );
        if (ass.data?.success) {
          setModuleAssessments((prev) => ({ ...prev, [moduleId]: ass.data.assessments || [] }));
        }
      } catch (_) {}
      try {
        const af = await api.get(
          `/qualifications/${qualificationId}/learning-content/bok-documents/assessment-files`,
          { params: { task_codes: codes.join(',') } }
        );
        if (af.data?.success && Array.isArray(af.data.files)) {
          setTaskAssessmentFileStatus((prev) => {
            const next = { ...prev };
            for (const f of af.data.files) {
              if (f.subtask_code) next[f.subtask_code] = Boolean(f.has_openai_file);
            }
            return next;
          });
        }
      } catch (_) {}
    },
    [qualificationId, assignedItems]
  );

  const isAssessmentsTabEnabled = useCallback(
    (moduleId) => {
      const assign = assignedItems[moduleId] || { KT: [], PS: [] };
      const items = [...(assign.KT || []), ...(assign.PS || [])];
      const saved = savedDocuments.subtaskCodes || [];
      const withSaved = items.filter((t) => saved.includes(t.code));
      if (withSaved.length === 0) return false;
      return withSaved.every((t) => Boolean(taskApprovals[t.code]));
    },
    [assignedItems, savedDocuments.subtaskCodes, taskApprovals]
  );

  useEffect(() => {
    if (modules.length === 0) return;
    const maxIdx = modules.length;
    if (activeTab > maxIdx) setActiveTab(maxIdx);
  }, [modules.length, activeTab]);

  useEffect(() => {
    if (activeTab >= modules.length) return;
    const mod = modules[activeTab];
    if (!mod) return;
    if (!isAssessmentsTabEnabled(mod.id) && (moduleSubTabs[mod.id] || 0) >= 1) {
      setModuleSubTabs((prev) => ({ ...prev, [mod.id]: 0 }));
    }
  }, [activeTab, modules, moduleSubTabs, isAssessmentsTabEnabled]);

  useEffect(() => {
    if (!qualificationId || !modules.length) return;
    if (activeTab >= modules.length) return;
    const mod = modules[activeTab];
    if (!mod) return;
    refreshModuleLearningMeta(mod.id);
  }, [qualificationId, activeTab, modules, refreshModuleLearningMeta]);
  
  const handleGenerate = async (module, parentTask) => {
    const taskCode = parentTask.code;
    setGenerating((prev) => ({ ...prev, [taskCode]: true }));
    setGeneratingPages((prev) => ({ ...prev, [taskCode]: [] }));
    
    try {
      // Build the full hierarchy JSON for OpenAI
      const hierarchyData = {
        program: {
          name: qualification?.title || '',
          code: qualification?.saqa_id || '',
          ...(qualification?.nqf_level != null && qualification?.nqf_level !== ''
            ? { nqf_level: qualification.nqf_level }
            : {}),
          total_credits: qualification?.total_credits || 0
        },
        module: {
          code: module.elo_number || `ELO${module.id}`,
          name: outcomeModuleDisplayName(module),
          description: module.description || '',
          credits: module.credits || 0,
          ...(parentTask.nqf_level != null ? { nqf_level: parentTask.nqf_level } : {})
        },
        task: {
          code: parentTask.code,
          title: parentTask.title,
          description: parentTask.description,
          type: parentTask.type, // 'KT' or 'PS'
          credits: parentTask.credits || 0,
          ...(parentTask.nqf_level != null ? { nqf_level: parentTask.nqf_level } : {})
        },
        subtasks: filterChildrenForContentGeneration(parentTask.children || [])
      };
      

      
      // Set up EventSource for streaming with query parameters
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        hierarchy_data: JSON.stringify(hierarchyData),
        depth: 'standard',
        format_preferences: JSON.stringify({ include_markdown: true, include_plaintext: false }),
        citation_style: 'harvard',
        subtask_code: taskCode,
        task_title: parentTask.title,
        task_description: parentTask.description || ''
      });
      
      // Add token as query parameter for EventSource authentication
      if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
        params.set('token', token);
      }
      
      const eventSource = new EventSource(
        `/api/qualifications/${qualificationId}/learning-content/generate-bok-pages?${params.toString()}`,
        { withCredentials: true }
      );
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          
          if (data.type === 'page_completed') {
            // Accumulate: append this page to the document (streaming display = full accumulated responses)
            setGeneratingPages(prev => {
              const list = [...(prev[taskCode] || [])];
              const newPage = data.page && (data.page.page_number != null) ? data.page : { ...data.page, page_number: list.length + 1 };
              list.push(newPage);
              list.sort((a, b) => (a.page_number ?? 0) - (b.page_number ?? 0));
              return { ...prev, [taskCode]: list };
            });
          }
          
          if (data.type === 'generation_complete') {
            eventSource.close();
            setGenerating((prev) => ({ ...prev, [taskCode]: false }));
            if (data.document) {
              setGeneratedContent((prev) => ({
                ...prev,
                [taskCode]: data.document
              }));
            }
          }
          
          if (data.type === 'error') {
            eventSource.close();
            setGenerating((prev) => ({ ...prev, [taskCode]: false }));
            const codeHint = data.code ? ` (${data.code})` : '';
            alert('Generation failed' + codeHint + ': ' + (data.message || 'Unknown error'));
          }
        } catch (parseError) {

        }
      };
      
      eventSource.onerror = (error) => {

        eventSource.close();
        setGenerating((prev) => ({ ...prev, [taskCode]: false }));
        alert('Connection error during generation');
      };
      
      // EventSource will handle the generation automatically
      
    } catch (e) {

      setGenerating((prev) => ({ ...prev, [taskCode]: false }));
      alert('Failed to generate content: ' + (e.response?.data?.message || e.message));
    }
  };
  
  const handleViewPages = (subtaskCode, taskTitle) => {
    setBokPageViewer({
      open: true,
      subtaskCode: subtaskCode,
      title: `BOK Pages: ${taskTitle}`
    });
  };
  
  const handleGenerateThumbnail = async (module, parentTask) => {
    const taskCode = parentTask.code;
    const taskTitle = parentTask.title;
    const taskDescription = parentTask.description;
    const taskType = parentTask.code?.startsWith('PM-') ? 'Practical Skills Task' : 'Knowledge Task';
    
    setGeneratingThumbnail((prev) => ({ ...prev, [taskCode]: true }));
    
    try {
      // Create hierarchy data for thumbnail generation
      const hierarchyData = {
        program: {
          name: qualification?.title || 'Learning programme',
          description: qualification?.description || 'Qualification programme'
        },
        module: {
          code: module.elo_number || module.id,
          name: outcomeModuleDisplayName(module),
          description: module.description || ''
        },
        task: {
          code: taskCode,
          title: taskTitle,
          description: taskDescription,
          type: taskType
        },
        subtasks: filterChildrenForContentGeneration(parentTask.children || []).map((child) => ({
          code: child.code,
          title: child.title,
          description: child.description || '',
          node_type: child.node_type
        }))
      };

      const response = await api.post(`/qualifications/${qualificationId}/learning-content/generate-thumbnail`, {
        hierarchy_data: hierarchyData,
        curriculum_name: qualification?.title || 'Learning programme',
        module_name: outcomeModuleDisplayName(module),
        task_code: taskCode,
        task_title: taskTitle,
        task_description: taskDescription,
        task_type: taskType
      });

      if (response.data?.success && (response.data?.thumbnail_data_url || response.data?.thumbnail_absolute_url || response.data?.thumbnail_url)) {
        const immediate =
          response.data.thumbnail_data_url ||
          response.data.thumbnail_absolute_url ||
          response.data.thumbnail_url;
        setThumbnails((prev) => ({ ...prev, [taskCode]: immediate }));
        try {
          const thumbnailsRes = await api.get(`/qualifications/${qualificationId}/learning-content/thumbnails`);
          if (thumbnailsRes.data?.success && thumbnailsRes.data.thumbnails) {
            setThumbnails((prev) => ({ ...prev, ...thumbnailsRes.data.thumbnails }));
          }
        } catch (_) {
          /* keep immediate src */
        }
      }
    } catch (e) {

      alert('Failed to generate thumbnail: ' + (e.response?.data?.message || e.message));
    } finally {
      setGeneratingThumbnail((prev) => ({ ...prev, [taskCode]: false }));
    }
  };
  
  const handleViewPDF = async (subtaskCode, subtaskTitle) => {
    if (viewingPdf[subtaskCode]) return;
    setViewingPdf((prev) => ({ ...prev, [subtaskCode]: true }));
    setSyncSnackbar({ open: true, message: 'Generating PDF — this can take several seconds…' });
    // Open during the click so the browser does not treat it as a blocked popup after the PDF render.
    const pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      try {
        pdfWindow.document.write(
          '<p style="font-family:sans-serif;padding:24px;color:#334155">Generating PDF…</p>'
        );
      } catch (_) {
        /* cross-origin or already navigated */
      }
    }
    try {
      const response = await api.get(`/qualifications/${qualificationId}/learning-content/saved-document/${encodeURIComponent(subtaskCode)}`);
      if (!response.data.success || !response.data.document || !response.data.document.html_content) {
        if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
        alert('No saved content found for this task. Save or generate content first, then try View PDF again.');
        return;
      }
      const htmlContent = response.data.document.html_content;
      const programmeName = qualification?.name || 'LEARNING PROGRAMME';
      const moduleName = subtaskTitle || 'Module Content';
      const pdfResponse = await api.post('/pdf/preview', {
        programmeName: programmeName.toUpperCase(),
        moduleName: moduleName,
        contentHtml: htmlContent
      }, { responseType: 'blob' });
      const blob = pdfResponse.data;
      if (!blob || blob.size === 0) {
        throw new Error('PDF generation returned empty file');
      }
      const url = window.URL.createObjectURL(blob);
      if (pdfWindow && !pdfWindow.closed) {
        pdfWindow.location = url;
        pdfWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(url);
        });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(programmeName || 'document').replace(/[^a-z0-9]/gi, '_')}-${(moduleName || 'content').replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setSyncSnackbar({
          open: true,
          message: 'Popup was blocked — the PDF was downloaded instead.'
        });
      }
      api
        .post(
          `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(subtaskCode)}/sync-assessment-file`,
          {}
        )
        .then((res) => {
          if (res.data?.success) {
            setTaskAssessmentFileStatus((prev) => ({ ...prev, [subtaskCode]: true }));
          }
        })
        .catch((err) => {
          setSyncSnackbar({
            open: true,
            message:
              err.response?.data?.message ||
              err.message ||
              'Could not sync BoK file for assessment generation'
          });
        });
    } catch (e) {
      if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
      if (e.response?.status === 404) {
        alert('No saved content found for this task. Save or generate content first, then try View PDF again.');
      } else {
        const msg = e.response?.data instanceof Blob
          ? (await e.response.data.text().catch(() => '')) || e.message
          : (e.response?.data?.message || e.message);
        alert('Failed to generate PDF: ' + msg);
      }
    } finally {
      setViewingPdf((prev) => ({ ...prev, [subtaskCode]: false }));
    }
  };

  const handleDownloadWord = async (subtaskCode, subtaskTitle) => {
    if (downloadingWord[subtaskCode]) return;
    setDownloadingWord((prev) => ({ ...prev, [subtaskCode]: true }));
    try {
      const response = await api.get(`/qualifications/${qualificationId}/learning-content/saved-document/${encodeURIComponent(subtaskCode)}`);
      if (!response.data.success || !response.data.document || !response.data.document.html_content) {
        alert('No saved content found for this task. Save or generate content first, then try Download Word again.');
        return;
      }
      const programmeName = qualification?.name || 'LEARNING PROGRAMME';
      const moduleName = subtaskTitle || 'Module Content';
      await downloadDocx({
        programmeName: programmeName.toUpperCase(),
        moduleName,
        contentHtml: response.data.document.html_content
      });
    } catch (e) {
      if (e.response?.status === 404) {
        alert('No saved content found for this task. Save or generate content first, then try Download Word again.');
      } else {
        alert('Failed to download Word document: ' + (e.message || 'Word export failed'));
      }
    } finally {
      setDownloadingWord((prev) => ({ ...prev, [subtaskCode]: false }));
    }
  };
  
  const handleViewSavedContent = async (subtaskCode, subtaskTitle) => {
    try {
      const response = await api.get(`/qualifications/${qualificationId}/learning-content/saved-document/${encodeURIComponent(subtaskCode)}`);
      const data = response.data;
      if (data.success && data.document) {
        setSavedContentModal({
          open: true,
          title: `Saved Content: ${subtaskTitle}`,
          moduleName: subtaskTitle || 'Module Content',
          content: data.document.html_content,
          runId: data.document.run_id || null,
          subtaskCode: subtaskCode,
          referencesPendingCompletion: Boolean(data.document.references_pending_completion),
          referencesDesignerNotice: data.document.references_designer_notice || null,
          wordCount: data.document.word_count || 0,
          pageCount: data.document.page_count || data.document.total_pages || 0,
          changeLog: Array.isArray(data.document.change_log) ? data.document.change_log : []
        });
      } else {
        alert('No saved content found for this task');
      }
    } catch (e) {
      if (e.response?.status === 404) {
        alert('No saved content found for this task. Please generate content first.');
      } else {
        alert('Failed to open saved content: ' + (e.response?.data?.message || e.message));
      }
    }
  };
  
  // Handle settings button click
  const handleSettingsClick = (moduleCode, moduleTitle) => {
    setSettingsModal({
      open: true,
      moduleCode: moduleCode,
      moduleTitle: moduleTitle
    });
  };

  // Handle thumbnail click to show in modal
  const handleThumbnailClick = (thumbnailUrl, taskTitle) => {
    setThumbnailModal({
      open: true,
      url: thumbnailUrl,
      title: `Thumbnail for ${taskTitle}`
    });
  };
  
  const handleToggleTaskApproval = async (taskCode, currentlyApproved) => {
    try {
      const res = await api.patch(
        `/qualifications/${qualificationId}/learning-content/bok-documents/${encodeURIComponent(taskCode)}/learning-approval`,
        { approved: !currentlyApproved }
      );
      const at = res.data?.learning_task_approved_at || null;
      setTaskApprovals((prev) => ({
        ...prev,
        [taskCode]: at
      }));
    } catch (e) {
      alert('Could not update task approval: ' + (e.response?.data?.message || e.message));
    }
  };

  const itemHasContent = useCallback((item) => {
    if (!item?.code) return false;
    return Boolean(generatedContent[item.code] || savedDocuments.subtaskCodes?.includes(item.code));
  }, [generatedContent, savedDocuments.subtaskCodes]);

  // Calculate progress
  const progress = useMemo(() => {
    let total = 0;
    let completed = 0;
    
    Object.values(assignedItems).forEach(assignment => {
      total += (assignment.KT?.length || 0) + (assignment.PS?.length || 0);
      assignment.KT?.forEach(item => {
        if (itemHasContent(item)) completed++;
      });
      assignment.PS?.forEach(item => {
        if (itemHasContent(item)) completed++;
      });
    });
    
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [assignedItems, itemHasContent]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (modules.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          No ELO outcomes found from Step 3 (ELO Mapping Canvas). Please complete Step 3 first.
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Step 5: Learning Content Creation
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
          Generate detailed learning content for each knowledge task and practical skill using AI
        </Typography>
        
        {/* Progress bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress.percent}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Chip 
            label={`${progress.completed}/${progress.total} (${progress.percent}%)`}
            color={progress.percent === 100 ? 'success' : 'primary'}
            size="small"
          />
        </Box>
      </Box>
      
      {/* Agents Builder Toggle */}
      <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Button
          variant={agentsBuilderTab ? "contained" : "outlined"}
          onClick={() => setAgentsBuilderTab(!agentsBuilderTab)}
          color="primary"
        >
          {agentsBuilderTab ? "← Back to Modules" : "🤖 Agents Builder"}
        </Button>
      </Box>

      {agentsBuilderTab ? (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <LearningBuilder 
            modules={modules}
            assignedItems={assignedItems}
            qualification={qualification}
          />
        </Box>
      ) : (
        <>
      {/* Module Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', position: 'relative' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
            },
          }}
        >
          {modules.map((module, idx) => {
            const assignments = assignedItems[module.id] || { KT: [], PS: [] };
            const totalItems = (assignments.KT?.length || 0) + (assignments.PS?.length || 0);
            const completedItems = [...(assignments.KT || []), ...(assignments.PS || [])].filter(
              item => itemHasContent(item)
            ).length;
            
            return (
              <Tab 
                key={module.id}
                value={idx}
                label={
                  <Box 
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}
                  >
                    <span>{outcomeModuleDisplayName(module, idx)}</span>
                    <Chip 
                      label={`${completedItems}/${totalItems}`}
                      size="small"
                      color={completedItems === totalItems && totalItems > 0 ? 'success' : 'default'}
                    />
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSettingsClick(
                          String(module.elo_number ?? module.id),
                          outcomeModuleDisplayName(module, idx)
                        );
                      }}
                      sx={{ 
                        ml: 0.5,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)'
                        }
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </Box>
                  </Box>
                }
              />
            );
          })}
          <Tab value={modules.length} label="Exam Module" />
        </Tabs>
      </Box>
      
      {/* Tab Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {activeTab === modules.length ? (
          <ProgramExamsPanel
            qualificationId={qualificationId}
            qualification={qualification}
            assignedItems={assignedItems}
            parentListVersion={programAssessmentListVersion}
            onListChanged={() => setProgramAssessmentListVersion((v) => v + 1)}
          />
        ) : (
        modules.map((module, idx) => {
          if (activeTab !== idx) return null;
          
          const assignments = assignedItems[module.id] || { KT: [], PS: [] };
          const ktItems = assignments.KT || [];
          const psItems = assignments.PS || [];
          const moduleSubTab = moduleSubTabs[module.id] || 0;
          
          const modAssessList = moduleAssessments[module.id] || [];
          const assessmentsGate = isAssessmentsTabEnabled(module.id);
          
          if (ktItems.length === 0 && psItems.length === 0) {
            return (
              <Alert key={module.id} severity="info">
                No knowledge tasks or practical skills assigned to this ELO outcome.
              </Alert>
            );
          }
          
          return (
            <Box key={module.id}>
              {/* Sub-tabs: Knowledge Tasks | Assessments | Assessment audit */}
              <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
                <Tabs 
                  value={moduleSubTab} 
                  onChange={(e, newValue) => setModuleSubTabs(prev => ({ ...prev, [module.id]: newValue }))}
                  variant="standard"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 48,
                      textTransform: 'none',
                    },
                    '& .MuiTab-wrapper': {
                      width: '100%',
                      alignItems: 'center',
                    },
                  }}
                >
                  <Tab 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <span>📚 Knowledge Tasks</span>
                        <Chip label={`${ktItems.length}`} size="small" color="primary" />
                      </Box>
                    }
                  />
                  <Tab
                    disabled={!assessmentsGate}
                    label={
                      <Tooltip
                        title={
                          assessmentsGate
                            ? ''
                            : 'Approve every task that has saved BoK content (and ensure thumbnails) before using Assessments.'
                        }
                      >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>Assessments</span>
                          <Chip label={`${modAssessList.length}`} size="small" color="secondary" />
                        </Box>
                      </Tooltip>
                    }
                  />
                  <Tab
                    disabled={!assessmentsGate}
                    label={
                      <Tooltip
                        title={
                          assessmentsGate
                            ? ''
                            : 'Approve every task that has saved BoK content (and ensure thumbnails) before opening Audit.'
                        }
                      >
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>Assessment audit</span>
                          <Chip label="IAC" size="small" color="info" variant="outlined" />
                        </Box>
                      </Tooltip>
                    }
                  />
                </Tabs>
              </Box>
              {!assessmentsGate && moduleSubTab === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The Assessments and Assessment audit tabs unlock when every task in this module that has saved BoK
                  content is approved (FactCheck after thumbnail).
                </Alert>
              )}
              
              {/* Knowledge Tasks Tab Content */}
              {moduleSubTab === 0 && (
                <Box>
                  {/* Knowledge Tasks Section */}
                  {ktItems.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
                        📚 Knowledge Tasks ({ktItems.length})
                      </Typography>
                  
                  <Stack spacing={2}>
                    {ktItems.map(item => {
                      const hasSavedContent = savedDocuments.subtaskCodes?.includes(item.code);
                      const hasContent = !!generatedContent[item.code] || hasSavedContent;
                      const contentSubtasks = filterChildrenForContentGeneration(item.children || []);
                      const taskApproved = Boolean(taskApprovals[item.code]);
                      const canApproveLearning = hasSavedContent && Boolean(thumbnails[item.code]);
                      const approveTip = !hasSavedContent
                        ? 'Save generated content first'
                        : !thumbnails[item.code]
                          ? 'Generate a thumbnail first'
                          : taskApproved
                            ? 'Task approved — click to revoke'
                            : 'Approve task for assessment generation';
                      
                      return (
                        <Card key={item.code} sx={{ border: '1px solid #e2e8f0' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              {/* Thumbnail Display */}
                              <Box sx={{ mr: 2, minWidth: '120px' }}>
                                {thumbnails[item.code] ? (
                                  <Box 
                                    sx={{ 
                                      width: 120, 
                                      height: 90, 
                                      borderRadius: 1, 
                                      overflow: 'hidden',
                                      border: '1px solid #e2e8f0',
                                      backgroundColor: '#f8fafc',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease-in-out',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                        border: '2px solid #3b82f6'
                                      }
                                    }}
                                    onClick={() => handleThumbnailClick(thumbnails[item.code], item.title)}
                                  >
                                    <img 
                                      src={thumbnails[item.code]} 
                                      alt={`Thumbnail for ${item.title}`}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box sx={{ 
                                    width: 120, 
                                    height: 90, 
                                    borderRadius: 1, 
                                    border: '2px dashed #cbd5e1',
                                    backgroundColor: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#64748b',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    p: 1
                                  }}>
                                    No thumbnail
                                  </Box>
                                )}
                              </Box>
                              
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip label={item.code} size="small" color="primary" />
                                  {hasContent && (
                                    <Chip label="✓ Generated" size="small" color="success" />
                                  )}
                                  {savedDocuments.documents?.[item.code]?.word_count > 0 && (
                                    <Chip
                                      label={`${Number(savedDocuments.documents[item.code].word_count).toLocaleString()} words`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {savedDocuments.documents?.[item.code]?.page_count > 0 && (
                                    <Chip
                                      label={`${savedDocuments.documents[item.code].page_count} page${savedDocuments.documents[item.code].page_count === 1 ? '' : 's'}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  <Chip label="Knowledge Task" size="small" variant="outlined" color="primary" />
                                  {contentSubtasks.length > 0 && (
                                    <Chip label={`${contentSubtasks.length} subtasks`} size="small" variant="outlined" />
                                  )}
                                  {taskApproved && (
                                    <Chip label="Task approved" size="small" color="success" variant="outlined" />
                                  )}
                                  {taskAssessmentFileStatus[item.code] && (
                                    <Chip label="AI file ready" size="small" color="info" variant="outlined" />
                                  )}
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                  {item.title}
                                </Typography>
                                {item.description && (
                                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    {item.description}
                                  </Typography>
                                )}
                                {item.credits > 0 && (
                                  <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                    {item.credits} credits
                                  </Typography>
                                )}
                                {Array.isArray(savedDocuments.documents?.[item.code]?.change_log) &&
                                  savedDocuments.documents[item.code].change_log.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                                      Document change log
                                    </Typography>
                                    {savedDocuments.documents[item.code].change_log.slice(-5).reverse().map((entry, logIdx) => (
                                      <Typography key={`${item.code}-log-${logIdx}`} variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                        {formatDocumentChange(entry)}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                                
                                {/* Show subtasks in accordion */}
                                {contentSubtasks.length > 0 && (
                                  <Accordion sx={{ mt: 2, boxShadow: 'none', border: '1px solid #e2e8f0' }}>
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      sx={{ 
                                        backgroundColor: '#f8fafc',
                                        '&:hover': { backgroundColor: '#f1f5f9' }
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                        View {contentSubtasks.length} subtasks
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 1 }}>
                                      <Stack spacing={1}>
                                        {contentSubtasks.map(child => (
                                          <Box key={child.code} sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1,
                                            backgroundColor: '#fafafa',
                                            borderRadius: 1,
                                            border: '1px solid #e5e7eb'
                                          }}>
                                            <Chip label={child.code} size="small" variant="outlined" />
                                            <Typography variant="caption" sx={{ color: '#64748b', flex: 1 }}>
                                              {child.title}
                                            </Typography>
                                            {child.node_type && (
                                              <Chip 
                                                label={child.node_type === 'topic_element' ? 'Topic' : 'Assessment'} 
                                                size="small" 
                                                variant="outlined"
                                                color={child.node_type === 'assessment_criterion' ? 'secondary' : 'default'}
                                              />
                                            )}
                                          </Box>
                                        ))}
                                      </Stack>
                                    </AccordionDetails>
                                  </Accordion>
                                )}
                              </Box>
                              
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Tooltip title="Generate thumbnail">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => handleGenerateThumbnail(module, item)}
                                      disabled={generatingThumbnail[item.code]}
                                      aria-label="Generate thumbnail"
                                    >
                                      {generatingThumbnail[item.code] ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <AutoAwesomeIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <TaskAgentBuilder
                                  task={item}
                                  module={module}
                                  qualification={qualification}
                                  showViewSavedButton={hasSavedContent}
                                  onViewSavedContent={handleViewSavedContent}
                                  onSaved={async () => {
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (_) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                  onCleared={async () => {
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (_) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                  onComplete={async (pages, taskCode) => {
                                    if (pages && pages.length > 0) {
                                      setGeneratedContent(prev => ({
                                        ...prev,
                                        [taskCode]: { pages }
                                      }));
                                      setGeneratingPages(prev => ({
                                        ...prev,
                                        [taskCode]: pages
                                      }));
                                    }
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (refreshError) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                />
                                {hasSavedContent && (
                                  <>
                                    <Tooltip title="View in editor">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleViewSavedContent(item.code, item.title)}
                                        aria-label="View in editor"
                                      >
                                        <EditNoteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={
                                        taskAssessmentFileStatus[item.code]
                                          ? 'View PDF (generated and synced for assessments)'
                                          : 'View PDF — not synced yet; open to generate and sync'
                                      }
                                    >
                                      <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        variant="dot"
                                        color={taskAssessmentFileStatus[item.code] ? 'success' : 'warning'}
                                      >
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={() => handleViewPDF(item.code, item.title)}
                                          disabled={Boolean(viewingPdf[item.code])}
                                          aria-label={
                                            taskAssessmentFileStatus[item.code]
                                              ? 'View PDF, synced'
                                              : 'View PDF, not synced yet'
                                          }
                                        >
                                          {viewingPdf[item.code] ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <PictureAsPdfIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Badge>
                                    </Tooltip>
                                    <Tooltip title="Download Word">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleDownloadWord(item.code, item.title)}
                                          disabled={Boolean(downloadingWord[item.code])}
                                          aria-label="Download Word"
                                        >
                                          {downloadingWord[item.code] ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <DescriptionIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                                <Tooltip title={approveTip}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color={taskApproved ? 'success' : canApproveLearning ? 'error' : 'default'}
                                      disabled={!taskApproved && !canApproveLearning}
                                      onClick={() => handleToggleTaskApproval(item.code, taskApproved)}
                                      aria-label={taskApproved ? 'Revoke task approval' : 'Approve task'}
                                    >
                                      <FactCheckIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              
              {/* Practical Skills Section */}
              {psItems.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
                    🛠️ Practical Skills ({psItems.length})
                  </Typography>
                  
                  <Stack spacing={2}>
                    {psItems.map(item => {
                      const hasSavedContent = savedDocuments.subtaskCodes?.includes(item.code);
                      const hasContent = !!generatedContent[item.code] || hasSavedContent;
                      const contentSubtasks = filterChildrenForContentGeneration(item.children || []);
                      const taskApproved = Boolean(taskApprovals[item.code]);
                      const canApproveLearning = hasSavedContent && Boolean(thumbnails[item.code]);
                      const approveTip = !hasSavedContent
                        ? 'Save generated content first'
                        : !thumbnails[item.code]
                          ? 'Generate a thumbnail first'
                          : taskApproved
                            ? 'Task approved — click to revoke'
                            : 'Approve task for assessment generation';
                      
                      return (
                        <Card key={item.code} sx={{ border: '1px solid #e2e8f0', bgcolor: '#fefce8' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              {/* Thumbnail Display */}
                              <Box sx={{ mr: 2, minWidth: '120px' }}>
                                {thumbnails[item.code] ? (
                                  <Box 
                                    sx={{ 
                                      width: 120, 
                                      height: 90, 
                                      borderRadius: 1, 
                                      overflow: 'hidden',
                                      border: '1px solid #e2e8f0',
                                      backgroundColor: '#f8fafc',
                                      cursor: 'pointer',
                                      transition: 'transform 0.2s ease-in-out',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                        border: '2px solid #3b82f6'
                                      }
                                    }}
                                    onClick={() => handleThumbnailClick(thumbnails[item.code], item.title)}
                                  >
                                    <img 
                                      src={thumbnails[item.code]} 
                                      alt={`Thumbnail for ${item.title}`}
                                      style={{ 
                                        width: '100%', 
                                        height: '100%', 
                                        objectFit: 'cover' 
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box sx={{ 
                                    width: 120, 
                                    height: 90, 
                                    borderRadius: 1, 
                                    border: '2px dashed #cbd5e1',
                                    backgroundColor: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#64748b',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    p: 1
                                  }}>
                                    No thumbnail
                                  </Box>
                                )}
                              </Box>
                              
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip label={item.code} size="small" sx={{ bgcolor: '#fbbf24', color: '#fff' }} />
                                  {hasContent && (
                                    <Chip label="✓ Generated" size="small" color="success" />
                                  )}
                                  {savedDocuments.documents?.[item.code]?.word_count > 0 && (
                                    <Chip
                                      label={`${Number(savedDocuments.documents[item.code].word_count).toLocaleString()} words`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {savedDocuments.documents?.[item.code]?.page_count > 0 && (
                                    <Chip
                                      label={`${savedDocuments.documents[item.code].page_count} page${savedDocuments.documents[item.code].page_count === 1 ? '' : 's'}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  <Chip label="Practical Skill" size="small" variant="outlined" sx={{ color: '#f59e0b' }} />
                                  {contentSubtasks.length > 0 && (
                                    <Chip label={`${contentSubtasks.length} subtasks`} size="small" variant="outlined" />
                                  )}
                                  {taskApproved && (
                                    <Chip label="Task approved" size="small" color="success" variant="outlined" />
                                  )}
                                  {taskAssessmentFileStatus[item.code] && (
                                    <Chip label="AI file ready" size="small" color="info" variant="outlined" />
                                  )}
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                  {item.title}
                                </Typography>
                                {item.description && (
                                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    {item.description}
                                  </Typography>
                                )}
                                {item.credits > 0 && (
                                  <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                                    {item.credits} credits
                                  </Typography>
                                )}
                                {Array.isArray(savedDocuments.documents?.[item.code]?.change_log) &&
                                  savedDocuments.documents[item.code].change_log.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
                                      Document change log
                                    </Typography>
                                    {savedDocuments.documents[item.code].change_log.slice(-5).reverse().map((entry, logIdx) => (
                                      <Typography key={`${item.code}-log-${logIdx}`} variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                                        {formatDocumentChange(entry)}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                                
                                {/* Show subtasks in accordion */}
                                {contentSubtasks.length > 0 && (
                                  <Accordion sx={{ mt: 2, boxShadow: 'none', border: '1px solid #fbbf24' }}>
                                    <AccordionSummary
                                      expandIcon={<ExpandMoreIcon />}
                                      sx={{ 
                                        backgroundColor: '#fefce8',
                                        '&:hover': { backgroundColor: '#fef3c7' }
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                        View {contentSubtasks.length} subtasks
                                      </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 1 }}>
                                      <Stack spacing={1}>
                                        {contentSubtasks.map(child => (
                                          <Box key={child.code} sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1,
                                            backgroundColor: '#fefce8',
                                            borderRadius: 1,
                                            border: '1px solid #fbbf24'
                                          }}>
                                            <Chip label={child.code} size="small" variant="outlined" />
                                            <Typography variant="caption" sx={{ color: '#64748b', flex: 1 }}>
                                              {child.title}
                                            </Typography>
                                            {child.node_type && (
                                              <Chip 
                                                label={child.node_type === 'topic_element' ? 'Topic' : 'Assessment'} 
                                                size="small" 
                                                variant="outlined"
                                                color={child.node_type === 'assessment_criterion' ? 'secondary' : 'default'}
                                              />
                                            )}
                                          </Box>
                                        ))}
                                      </Stack>
                                    </AccordionDetails>
                                  </Accordion>
                                )}
                              </Box>
                              
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Tooltip title="Generate thumbnail">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => handleGenerateThumbnail(module, item)}
                                      disabled={generatingThumbnail[item.code]}
                                      aria-label="Generate thumbnail"
                                    >
                                      {generatingThumbnail[item.code] ? (
                                        <CircularProgress size={18} />
                                      ) : (
                                        <AutoAwesomeIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <TaskAgentBuilder
                                  task={item}
                                  module={module}
                                  qualification={qualification}
                                  showViewSavedButton={hasSavedContent}
                                  onViewSavedContent={handleViewSavedContent}
                                  onSaved={async () => {
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (_) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                  onCleared={async () => {
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (_) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                  onComplete={async (pages, taskCode) => {
                                    if (pages && pages.length > 0) {
                                      setGeneratedContent(prev => ({
                                        ...prev,
                                        [taskCode]: { pages }
                                      }));
                                      setGeneratingPages(prev => ({
                                        ...prev,
                                        [taskCode]: pages
                                      }));
                                    }
                                    try {
                                      const savedListRes = await api.get(`/qualifications/${qualificationId}/learning-content/saved-documents`);
                                      if (savedListRes.data?.success && Array.isArray(savedListRes.data.subtaskCodes)) {
                                        setSavedDocuments({
                                          subtaskCodes: savedListRes.data.subtaskCodes,
                                          documents: savedListRes.data.documents || {}
                                        });
                                      }
                                    } catch (refreshError) {}
                                    await refreshModuleLearningMeta(module.id);
                                  }}
                                />
                                {hasSavedContent && (
                                  <>
                                    <Tooltip title="View in editor">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleViewSavedContent(item.code, item.title)}
                                        aria-label="View in editor"
                                      >
                                        <EditNoteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={
                                        taskAssessmentFileStatus[item.code]
                                          ? 'View PDF (generated and synced for assessments)'
                                          : 'View PDF — not synced yet; open to generate and sync'
                                      }
                                    >
                                      <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        variant="dot"
                                        color={taskAssessmentFileStatus[item.code] ? 'success' : 'warning'}
                                      >
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={() => handleViewPDF(item.code, item.title)}
                                          disabled={Boolean(viewingPdf[item.code])}
                                          aria-label={
                                            taskAssessmentFileStatus[item.code]
                                              ? 'View PDF, synced'
                                              : 'View PDF, not synced yet'
                                          }
                                        >
                                          {viewingPdf[item.code] ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <PictureAsPdfIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Badge>
                                    </Tooltip>
                                    <Tooltip title="Download Word">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleDownloadWord(item.code, item.title)}
                                          disabled={Boolean(downloadingWord[item.code])}
                                          aria-label="Download Word"
                                        >
                                          {downloadingWord[item.code] ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <DescriptionIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                                <Tooltip title={approveTip}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      color={taskApproved ? 'success' : canApproveLearning ? 'error' : 'default'}
                                      disabled={!taskApproved && !canApproveLearning}
                                      onClick={() => handleToggleTaskApproval(item.code, taskApproved)}
                                      aria-label={taskApproved ? 'Revoke task approval' : 'Approve task'}
                                    >
                                      <FactCheckIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              )}
                </Box>
              )}
              
              {moduleSubTab === 1 && assessmentsGate && (
                <ModuleAssessmentsTab
                  qualificationId={qualificationId}
                  module={module}
                  parentAssessmentListVersion={moduleAssessments[module.id]}
                  onOpenTaskAssessmentWizard={() =>
                    setWizardState({ open: true, task: null, moduleId: module.id })
                  }
                  onAssessmentsChanged={() => {
                    if (module?.id != null) refreshModuleLearningMeta(module.id);
                  }}
                />
              )}
              {moduleSubTab === 2 && assessmentsGate && (
                <ModuleAssessmentAuditPanel
                  qualificationId={qualificationId}
                  module={module}
                  assignedItems={assignedItems}
                  parentAssessmentListVersion={moduleAssessments[module.id]}
                />
              )}
            </Box>
          );
        })
        )}
      </Box>
      
      {/* PDF Viewer Modal */}
      <LearningContentPDFViewer
        open={pdfViewer.open}
        onClose={() => setPdfViewer({ open: false, url: null, title: '', subtaskCode: '', savedContent: null })}
        pdfUrl={pdfViewer.url}
        subtaskTitle={pdfViewer.title}
        subtaskCode={pdfViewer.subtaskCode}
        qualificationId={qualificationId}
        savedContent={pdfViewer.savedContent}
      />
      
      {/* Module Settings Modal */}
      <ModuleSettingsModal
        open={settingsModal.open}
        onClose={() => setSettingsModal({ open: false, moduleCode: '', moduleTitle: '' })}
        moduleCode={settingsModal.moduleCode}
        moduleTitle={settingsModal.moduleTitle}
        qualificationId={qualificationId}
      />
      
      {/* Thumbnail Modal */}
      <Modal
        open={thumbnailModal.open}
        onClose={() => setThumbnailModal({ open: false, url: null, title: '' })}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden'
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setThumbnailModal({ open: false, url: null, title: '' })}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          {/* Modal Title */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: 1,
              zIndex: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {thumbnailModal.title}
            </Typography>
          </Box>
          
          {/* Full-size Image */}
          {thumbnailModal.url && (
            <img
              src={thumbnailModal.url}
              alt={thumbnailModal.title}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </Box>
      </Modal>
      
      {/* BOK Page Viewer Modal */}
      <BokPageViewerModal
        open={bokPageViewer.open}
        onClose={() => setBokPageViewer({ open: false, subtaskCode: '', title: '' })}
        subtaskCode={bokPageViewer.subtaskCode}
        qualificationId={qualificationId}
        title={bokPageViewer.title}
      />
      
      {/* Saved Content Modal with Full CKEditor */}
      <SavedContentModal
        open={savedContentModal.open}
        onClose={() =>
          setSavedContentModal({
            open: false,
            title: '',
            moduleName: '',
            content: '',
            runId: null,
            subtaskCode: null,
            referencesPendingCompletion: false,
            referencesDesignerNotice: null,
            wordCount: 0,
            pageCount: 0,
            changeLog: []
          })
        }
        title={savedContentModal.title}
        content={savedContentModal.content}
        programmeName={(qualification?.name || 'LEARNING PROGRAMME').toUpperCase()}
        moduleName={savedContentModal.moduleName || 'Module Content'}
        runId={savedContentModal.runId}
        qualificationId={qualificationId}
        subtaskCode={savedContentModal.subtaskCode}
        referencesPendingCompletion={savedContentModal.referencesPendingCompletion}
        referencesDesignerNotice={savedContentModal.referencesDesignerNotice}
        wordCount={savedContentModal.wordCount}
        pageCount={savedContentModal.pageCount}
        changeLog={savedContentModal.changeLog}
        onSave={(result) => {
          // Optionally refresh or update UI
        }}
      />

      <TaskAssessmentWizardModal
        open={wizardState.open}
        onClose={() => setWizardState({ open: false, task: null, moduleId: null })}
        qualificationId={qualificationId}
        moduleId={wizardState.moduleId}
        assignedItems={assignedItems}
        task={wizardState.task}
        onSaved={async () => {
          if (wizardState.moduleId != null) {
            await refreshModuleLearningMeta(wizardState.moduleId);
          }
        }}
      />
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setSyncSnackbar({ open: false, message: '' })}
        message={syncSnackbar.message}
      />
        </>
      )}
    </Box>
  );
}

